/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  useParams,
  useRouteError
} from 'react-router';
import {
  BaseLayout,
  BaseLayoutV2,
  ETicketContainer,
  BookingContainer,
  TicketContainer
} from '@link/portal/layouts';
import { Loading } from '@linktivity/link-ui';
import { LIMOUSINE_SUPPLIER_IDS } from '@link/portal/views/Limousine/constants';

const Booking = lazy(() => import('@link/portal/views/Booking'));
const BookingDetails = lazy(() => import('@link/portal/views/BookingDetails'));
const TicketDetails = lazy(() => import('@link/portal/views/TicketDetails'));
const TicketHistory = lazy(() => import('@link/portal/views/TicketHistory'));
const Advanced = lazy(() => import('@link/portal/views/Advanced'));
const NotFound = lazy(() => import('@link/portal/views/NotFound'));
const VoucherDetails = lazy(() => import('@link/portal/views/VoucherDetails'));
const VoucherList = lazy(() => import('@link/portal/views/VoucherList'));
const VoucherListV2 = lazy(() => import('@link/portal/views/VoucherListV2'));
const VoucherDetailsV2 = lazy(
  () => import('@link/portal/views/VoucherDetailsV2')
);
const Limousine = lazy(() => import('@link/portal/views/Limousine'));

const TicketIndex = () => {
  const { supplierId } = useParams();
  return LIMOUSINE_SUPPLIER_IDS.includes(supplierId ?? '') ? (
    <Limousine />
  ) : (
    <TicketDetails />
  );
};

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return (
    <Suspense
      fallback={
        <div className="loading">
          <Loading />
        </div>
      }
    >
      {view}
    </Suspense>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route>
        <Route path="/" element={<BaseLayout />}>
          <Route index element={<NotFound />} />
          <Route element={<ETicketContainer />}>
            <Route
              path="issueticket/:supplierId/:secret/:bookingId"
              element={<BookingContainer />}
            >
              <Route index element={lazyView(<Booking />)} />
              <Route path="details" element={lazyView(<BookingDetails />)} />
            </Route>
            <Route
              path="issueticket/:supplierId/:secret/:bookingId/:ticketId/:distributionId?"
              element={<TicketContainer />}
            >
              <Route index element={lazyView(<TicketIndex />)} />
              <Route path="history" element={lazyView(<TicketHistory />)} />
              <Route path="advanced" element={lazyView(<Advanced />)} />
            </Route>
          </Route>
          <Route path="voucher">
            <Route
              path=":bookingId/:secret"
              element={lazyView(<VoucherList />)}
            />
            <Route
              // for compatibility with legacy links like '/voucher/:bookingId/:secret/mobile.html'
              path=":bookingId/:secret/:fileName"
              element={lazyView(<VoucherList />)}
            />
            <Route
              path="detail/:bookingId/:secret"
              element={lazyView(<VoucherDetails />)}
            />
          </Route>
        </Route>
        <Route path="/" element={<BaseLayoutV2 />}>
          <Route path="voucher">
            <Route path="v2">
              <Route
                path=":bookingCustomId/:publicId"
                element={lazyView(<VoucherListV2 />)}
              />
              <Route
                path="detail/:bookingCustomId/:publicId"
                element={lazyView(<VoucherDetailsV2 />)}
              />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={lazyView(<NotFound />)} />
    </Route>
  )
);

export default router;
